/**
 * Grammar catalogue, AST types, and operator/keyword constants for the IF-condition Logic Wizard.
 *
 * Source-of-truth references:
 * - Backend\DataQ_Engine\Engine_Utils\MetaQ_PremiseEval.cpp:228-265 (operator registration)
 * - Backend\DataQ_Engine\Engine_Utils\KeywordIDs.cpp:18-94 (reserved-word table)
 * - DATA(Q) Format Guide_08042024.pdf, "Logical conditions" section (operator-by-question-type table)
 */

// ─── Question types ──────────────────────────────────────────────────────────

export enum QuestionType {
  FILL_IN_NUMBER = "FILL_IN_NUMBER",
  FILL_IN_REAL = "FILL_IN_REAL",
  FILL_IN_TEXT = "FILL_IN_TEXT",
  SELECT_ONE = "SELECT_ONE",
  SELECT_MULTIPLE = "SELECT_MULTIPLE",
  SELECT_RANGE = "SELECT_RANGE",
  GRID = "GRID",
  HIDDEN = "HIDDEN",
  UNKNOWN = "UNKNOWN",
}

// ─── Operators ───────────────────────────────────────────────────────────────

/**
 * Comparison operators: precedence 2 in the engine.
 *
 * Note: the engine also accepts `!=` as a synonym of `<>`; the parser
 * normalises any incoming `!=` to `<>` so the AST has a single canonical
 * representation and the operator dropdown isn't cluttered with duplicates.
 */
export type ComparisonOp =
  | "="
  | "IS"
  | "<>"
  | "IS NOT"
  | "<"
  | "<="
  | ">"
  | ">="

/** Set/membership operators: precedence 2 in the engine. */
export type MembershipOp =
  | "IN"
  | "ANY OF"
  | "NOT IN"
  | "NONE OF"
  | "ALL OF"

/** Group operators: precedence 1 in the engine. */
export type GroupOp = "AND" | "OR"

/** All comparison operators in canonical UI order. */
export const COMPARISON_OPS: ComparisonOp[] = [
  "IS",
  "IS NOT",
  "=",
  "<>",
  "<",
  "<=",
  ">",
  ">=",
]

export const MEMBERSHIP_OPS: MembershipOp[] = [
  "ANY OF",
  "NONE OF",
  "ALL OF",
  "IN",
  "NOT IN",
]

/** Display labels for the operator dropdown. */
export const OPERATOR_LABELS: Record<ComparisonOp | MembershipOp, string> = {
  IS: "is",
  "IS NOT": "is not",
  "=": "= (equals)",
  "<>": "≠ (not equal)",
  "<": "< (less than)",
  "<=": "≤ (less than or equal)",
  ">": "> (greater than)",
  ">=": "≥ (greater than or equal)",
  "ANY OF": "any of",
  "NONE OF": "none of",
  "ALL OF": "all of",
  IN: "in",
  "NOT IN": "not in",
}

// ─── Reserved words (cannot be used as bare label names) ─────────────────────
// Mirrored from Backend\DataQ_Engine\Engine_Utils\KeywordIDs.cpp:18-94.
// These are the keywords the parser/evaluator will treat specially.
export const RESERVED_KEYWORDS: ReadonlySet<string> = new Set([
  // Definition (DEF_*)
  "SURVEY", "NOTE", "TEXT", "EMBED", "CALCULATE", "SECTION", "EXCEPTIONS",
  "LIST", "RANGE", "Q", "QUESTION", "HIDDEN", "RECODE", "DATA", "IF", "FOR", "EACH",
  "LOCATION", "RANDOMIZE", "RANDOMISE", "REDEFINE", "VARIANT", "HIDE",
  "EXTEND", "ADJUST", "TRIM", "INSERT", "AT", "AFTER", "BEGIN", "END",
  "UNRECOGNIZED", "LANGUAGE", "INCLUDE", "CHANGE", "ORDERED",
  // Specification (SPEC_*)
  "ADD", "REMOVE", "PER", "SELECT", "ONE", "MANY", "MULTIPLE", "FROM",
  "SKIP", "TO", "TERMINATE", "FILLIN", "FILL", "IN", "WHERE", "AS",
  "VALIDATE", "SEP", "ID", "COMBINE", "HEADER", "MEDIA", "ELEM", "REMARK",
  "NUMBER", "REAL",
  // Logic rules (LOGRULE_*)
  "AND", "OR", "NOT", "IS", "ANY", "ALL", "NONE", "OF", "COUNT",
  "PRIORITY", "RANDOM", "UNION", "INTERSECT",
  // Pragmas (PRAGMA_*)
  "DEFINE", "DESIGN", "STOP", "PARSING", "LINE",
  // Properties / structures often seen
  "SELECTED",
])

/** Returns true if `name` (a single token) is a reserved word in the format. */
export function isReservedWord(name: string): boolean {
  return RESERVED_KEYWORDS.has(name.toUpperCase())
}

/**
 * Returns true if `name` can be safely emitted without `<<...>>` quoting.
 * Bare-label rule: must start with a letter, contain only [A-Za-z0-9_],
 * and not be a reserved word. (We always quote anyway in the generator —
 * this helper is for parser-side classification.)
 */
export function isBareLabelSafe(name: string): boolean {
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) return false
  return !isReservedWord(name)
}

// ─── AST ─────────────────────────────────────────────────────────────────────

export interface QRefOperand {
  kind: "qref"
  /** The label as it appears in the source (without `<<...>>`). */
  label: string
}

export interface ARefOperand {
  kind: "aref"
  /**
   * Answer-option text or list-member text. Often multi-word — the generator
   * always wraps in `<<...>>`. For `<<Yes>>`, label is `Yes`.
   */
  label: string
}

export interface NumOperand {
  kind: "num"
  value: number
}

export interface SetOperand {
  kind: "set"
  items: Operand[]
}

export interface CountOperand {
  kind: "count"
  /** Argument: a question reference, optionally wrapped in WHERE. */
  arg: QRefOperand | WhereOperand
}

export interface WhereOperand {
  kind: "where"
  question: QRefOperand
  /** Optional axis disambiguation (PER row / SELECT column). */
  axis?: "PER" | "SELECT"
  key: ARefOperand | NumOperand
}

export type Operand =
  | QRefOperand
  | ARefOperand
  | NumOperand
  | SetOperand
  | CountOperand
  | WhereOperand

// AST nodes representing boolean (combined) expressions:

export interface ComparisonNode {
  kind: "cmp"
  left: Operand
  op: ComparisonOp
  right: Operand
}

export interface MembershipNode {
  kind: "set-cmp"
  left: Operand
  op: MembershipOp
  right: SetOperand
}

export interface GroupNode {
  kind: "group"
  op: GroupOp
  children: ASTNode[]
}

export interface NotNode {
  kind: "not"
  child: ASTNode
}

/**
 * Placeholder leaf used by the UI while the user is still building a row.
 * The wizard's "Apply" button is disabled while any placeholder remains.
 */
export interface PlaceholderNode {
  kind: "placeholder"
}

export type ASTNode =
  | ComparisonNode
  | MembershipNode
  | GroupNode
  | NotNode
  | PlaceholderNode

// ─── Operator filtering by question type ─────────────────────────────────────

export interface OperatorAvailability {
  comparison: ComparisonOp[]
  membership: MembershipOp[]
  /** Whether `COUNT(question) <op> n` is permitted on this question. */
  countSupported: boolean
  /** Whether `WHERE` filtering is permitted (grids only). */
  whereSupported: boolean
}

const NUMERIC_COMPARISONS: ComparisonOp[] = ["=", "<>", "<", "<=", ">", ">="]
const NOMINAL_EQUALITY: ComparisonOp[] = ["IS", "IS NOT", "=", "<>"]
const ORDINAL_COMPARISONS: ComparisonOp[] = [
  "IS", "IS NOT", "=", "<>", "<", "<=", ">", ">=",
]

/**
 * Returns the operators valid for a question of the given type.
 *
 * @param qtype       Question type from `questionTypeDetector`.
 * @param isOrdinal   True if the question's list/range is ordered (allows `<`/`>`).
 */
export function getAllowedOperators(
  qtype: QuestionType,
  isOrdinal = false,
): OperatorAvailability {
  switch (qtype) {
    case QuestionType.FILL_IN_NUMBER:
    case QuestionType.FILL_IN_REAL:
      return {
        comparison: NUMERIC_COMPARISONS,
        membership: [],
        countSupported: false,
        whereSupported: false,
      }
    case QuestionType.FILL_IN_TEXT:
      return {
        comparison: [],
        membership: [],
        countSupported: false,
        whereSupported: false,
      }
    case QuestionType.SELECT_ONE:
    case QuestionType.SELECT_RANGE:
      return {
        comparison: isOrdinal || qtype === QuestionType.SELECT_RANGE
          ? ORDINAL_COMPARISONS
          : NOMINAL_EQUALITY,
        membership: ["ANY OF", "NONE OF", "IN", "NOT IN"],
        countSupported: false,
        whereSupported: false,
      }
    case QuestionType.SELECT_MULTIPLE:
      return {
        comparison: isOrdinal ? ORDINAL_COMPARISONS : NOMINAL_EQUALITY,
        membership: ["ANY OF", "ALL OF", "NONE OF", "IN", "NOT IN"],
        countSupported: true,
        whereSupported: false,
      }
    case QuestionType.GRID:
      return {
        comparison: ORDINAL_COMPARISONS,
        membership: ["ANY OF", "ALL OF", "NONE OF", "IN", "NOT IN"],
        countSupported: true,
        whereSupported: true,
      }
    case QuestionType.HIDDEN:
      // HIDDEN is a wrapper — we treat it permissively and let runtime resolution validate.
      return {
        comparison: ORDINAL_COMPARISONS,
        membership: ["ANY OF", "ALL OF", "NONE OF", "IN", "NOT IN"],
        countSupported: false,
        whereSupported: false,
      }
    case QuestionType.UNKNOWN:
    default:
      // Permissive fallback — we don't know the type, so allow everything.
      return {
        comparison: ORDINAL_COMPARISONS,
        membership: ["ANY OF", "ALL OF", "NONE OF", "IN", "NOT IN"],
        countSupported: true,
        whereSupported: true,
      }
  }
}

/** Human-readable label for a question type, for the picker tag. */
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  [QuestionType.FILL_IN_NUMBER]: "Number",
  [QuestionType.FILL_IN_REAL]: "Real",
  [QuestionType.FILL_IN_TEXT]: "Text",
  [QuestionType.SELECT_ONE]: "Single select",
  [QuestionType.SELECT_MULTIPLE]: "Multi select",
  [QuestionType.SELECT_RANGE]: "Range",
  [QuestionType.GRID]: "Grid",
  [QuestionType.HIDDEN]: "Hidden",
  [QuestionType.UNKNOWN]: "Unknown",
}

// ─── Helpers shared between parser and generator ────────────────────────────

/** Wraps `label` in `<<...>>` (canonical form). Idempotent. */
export function quoteLabel(label: string): string {
  const trimmed = label.trim()
  if (trimmed.startsWith("<<") && trimmed.endsWith(">>")) return trimmed
  return `<<${trimmed}>>`
}

/** Strips one layer of `<<...>>` if present. */
export function unquoteLabel(label: string): string {
  const trimmed = label.trim()
  if (trimmed.startsWith("<<") && trimmed.endsWith(">>")) {
    return trimmed.slice(2, -2)
  }
  return trimmed
}
